import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePageContext } from 'vike-react/usePageContext';
import { setSiteId, setHeaderOrganizationId } from 'src/api/baseApi';
import { setSiteContext } from 'src/analytics/mixpanel';

export type SiteConfig = {
   id: string;
   slug: string;
   code: string;
   displayName: string;
   primaryDomain: string;
   envDomains: { stg: string; prod: string; development: string };
   visualBranding: {
      seo: {
         seoKeyword: string;
         description: string;
         defaultTitle: string;
      };
      legal: {
         aboutLabel: string;
         aboutUrl: string;
         whoWeAreLabel: string;
         whoWeAreUrl: string;
         forSiteNameLabel: string;
         forSiteNameUrl: string;
         faqsLabel: string;
         faqsUrl: string;
         privacyPolicyLabel: string;
         privacyPolicyUrl: string;
         termsOfServiceLabel: string;
         termsOfServiceUrl: string;
      };
      pallate: {
         colorPrimary: string;
         colorSecondary: string;
         colorCallToAction: string;
         colorLightPrimary: string;
         colorTertiary: string;
      };
      primaryLogo: string;
      primaryLightLogo: string;
      footerLogo: string;
      favicon: string;
      homePage: {
         hero: {
            heroHeading: string;
            heroDescription: string;
            placeholderForDropdown: string;
            tagLines: string[];
            ctaLabel: string;
            ctaUrl: string;
         };
         instructional: {
            instructionalText: string;
            benefits: {
               key: string;
               caption: string;
            }[];
         };
         metric: {
            metrics: {
               key: string;
               caption: string;
            }[];
         };
         whoWeAre: {
            missionStatement: string;
            coreMission: string;
         };
         commonQuestions?: { question: string; answer: string }[];
      };
   };
   defaultLandingVideo: {
      orgCustomVideo: string;
      globalAdminVideo: string;
      siteDefaultFallback: string;
      siteDefaultFallbackCaption: string;
      siteDefaultFallbackAudioDescription: string;
      orgCustomVideoPath: string;
      globalAdminVideoPath: string;
      siteDefaultFallbackPath: string;
   };
   email: string;
   supportEmail?: string;
   organizations?: any;
   phoneNumber: string;
};

type SiteContextType = {
   siteConfig: SiteConfig | null;
   setConfig: (config: SiteConfig | null) => void;
};

const SiteContext = createContext<SiteContextType>({
   siteConfig: null,
   setConfig: () => {},
});

export const SiteGuardProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
   const pageContext = usePageContext() as any;
   const serverSiteId = pageContext?.site?.id;
   const [config, setConfig] = useState<SiteConfig | null>(pageContext?.site ?? null);
   const currPathname = window.location.pathname;

   const handleSetConfig = useCallback((newConfig: SiteConfig | null) => {
      setConfig(newConfig);
   }, []);

   useEffect(() => {
      const siteConfig = pageContext?.site;
      if (siteConfig) {
         setConfig(prev => {
            if (prev?.id === siteConfig.id) return prev;
            return siteConfig;
         });
         setSiteId(siteConfig.id);
         setSiteContext({
            id: siteConfig.id,
            code: siteConfig.code,
            slug: siteConfig.slug,
            displayName: siteConfig.displayName,
         });
      }
   }, [serverSiteId]);

   useEffect(() => {
      if (currPathname?.startsWith('/c/')) setHeaderOrganizationId(currPathname.split('/')[2]);
   }, [currPathname]);

   const contextValue = useMemo(
      () => ({ siteConfig: config, setConfig: handleSetConfig }),
      [config, handleSetConfig]
   );

   return <SiteContext.Provider value={contextValue}>{children}</SiteContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- co-located with its provider; splitting would ripple through every import site
export const useSiteConfig = () => {
   const context = useContext(SiteContext);
   if (!context) throw new Error('useSiteConfig must be used within SiteGuardProvider');

   return context;
};
